import React from 'react';
import { useTranslation } from 'react-i18next';
import type ReactAvatarEditor from 'react-avatar-editor';

type UseCropImageFileProps = {
  file: File | undefined;
  onChange: (data: string) => void;
  isAvatar?: boolean;
  isCalculateDimensions?: boolean;
};

type UseCropImageFileReturn = {
  borderRadius: number;
  height: number;
  rotate: number;
  scale: number;
  width: number;
  getImageCanva: () => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  setEditorRef: (editor: ReactAvatarEditor | null) => void;
  t: (key: string) => string;
};

function useCropImageFile({ file, onChange, isAvatar = false, isCalculateDimensions = false }: UseCropImageFileProps): UseCropImageFileReturn {
  const { t } = useTranslation();
  const minScale = 100;
  const maxScale = 500;
  const [editor, setEditor] = React.useState<ReactAvatarEditor | null>(null);
  const [scale, setScale] = React.useState<number>(minScale);
  const [height, setHeight] = React.useState<number>(250);
  const [width, setWidth] = React.useState<number>(250);
  const borderRadius = isAvatar ? 100 : 0;
  const rotate = 0;

  React.useEffect(loadImage, [file]);

  function setEditorRef(data: ReactAvatarEditor | null): void {
    setEditor(data);
  }

  function getImageCanva(): void {
    if (!editor) return;
    const canvas = editor.getImageScaledToCanvas();
    onChange(canvas.toDataURL());
  }

  const handleZoomIn = (): void => {
    if (scale >= minScale && scale < maxScale) {
      setScale(scale + 10);
      getImageCanva();
    }
  };

  const handleZoomOut = (): void => {
    if (scale > minScale && scale <= maxScale) {
      setScale(scale - 10);
      getImageCanva();
    }
  };

  function loadImage(): void {
    if (file && isCalculateDimensions) {
      const _URL = window.URL || window.webkitURL;
      const img = new Image();

      img.src = _URL.createObjectURL(file);
      img.onload = function (): void {
        const widthImg = img.width > 1000 ? img.width / 10 : img.width;
        const heightImg = img.height > 1000 ? img.height / 10 : img.height;
        setHeight(heightImg);
        setWidth(widthImg);
      };
    }
  }

  return {
    borderRadius,
    height,
    rotate,
    scale,
    width,
    getImageCanva,
    handleZoomIn,
    handleZoomOut,
    setEditorRef,
    t,
  };
}

export default useCropImageFile;
